import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { motion, AnimatePresence } from "framer-motion";
import ParticleBackground from "@/components/ParticleBackground";
import Navbar from "@/components/Navbar";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProductLanding from "@/pages/ProductLanding";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import OnboardingWizard from "@/pages/OnboardingWizard";
import ScoreDashboard from "@/pages/ScoreDashboard";
import LenderReport from "@/pages/LenderReport";
import PersonaEstimate from "@/pages/PersonaEstimate";
import PeerComparison from "@/pages/PeerComparison";
import ImprovementRoadmap from "@/pages/ImprovementRoadmap";
import AdminConsole from "@/pages/AdminConsole";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AdminScoreDetail from "@/pages/AdminScoreDetail";
import ScoreHistory from "@/pages/ScoreHistory";
import ScoreHistoryDetail from "@/pages/ScoreHistoryDetail";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";
import VibeChat from "@/components/VibeChat";
import ScrollManager from "@/components/ScrollManager";
import { ScrollProgress } from "@/components/animations";
import { VARIANTS } from "@/lib/motionSystem";

function RouteBackground() {
    const location = useLocation();
    const executiveMode = location.pathname.includes("/genz-credit-scoring/lender/")
        || location.pathname.includes("/genz-credit-scoring/admin");

    if (executiveMode) {
        return (
            <div
                className="fixed inset-0 z-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle at 10% 0%, rgba(99,102,241,0.08), transparent 40%), radial-gradient(circle at 90% 100%, rgba(14,165,233,0.08), transparent 45%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
                }}
            />
        );
    }

    return (
        <>
            <ParticleBackground />
            <div
                className="fixed inset-0 z-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle at 20% 10%, rgba(99,102,241,0.14), transparent 35%), radial-gradient(circle at 80% 0%, rgba(6,182,212,0.1), transparent 35%), radial-gradient(circle at 70% 80%, rgba(168,85,247,0.08), transparent 35%)",
                }}
            />
        </>
    );
}

function AnimatedRoutes() {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={location.pathname}
                initial={VARIANTS.page.initial}
                animate={VARIANTS.page.animate}
                exit={VARIANTS.page.exit}
            >
                <ErrorBoundary key={location.pathname}>
                    <Routes location={location}>
                        <Route path="/" element={<ProductLanding />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/genz-credit-scoring" element={<ProductLanding />} />
                        <Route path="/genz-credit-scoring/flow" element={<OnboardingWizard />} />
                        <Route path="/genz-credit-scoring/result/:id" element={<ScoreAccessRoute><ScoreDashboard /></ScoreAccessRoute>} />
                        <Route path="/genz-credit-scoring/lender/:id" element={<ScoreAccessRoute><LenderReport /></ScoreAccessRoute>} />
                        <Route path="/genz-credit-scoring/persona" element={<PersonaEstimate />} />
                        <Route path="/genz-credit-scoring/compare" element={<PeerComparison />} />
                        <Route path="/genz-credit-scoring/improve" element={<ImprovementRoadmap />} />
                        <Route path="/genz-credit-scoring/admin" element={<ProtectedRoute requireAdmin><AdminConsole /></ProtectedRoute>} />
                        <Route path="/genz-credit-scoring/admin/users/:userId" element={<ProtectedRoute requireAdmin><AdminUserDetail /></ProtectedRoute>} />
                        <Route path="/genz-credit-scoring/admin/scores/:scoreId" element={<ProtectedRoute requireAdmin><AdminScoreDetail /></ProtectedRoute>} />
                        <Route path="/genz-credit-scoring/history" element={<ProtectedRoute><ScoreHistory /></ProtectedRoute>} />
                        <Route path="/genz-credit-scoring/history/:id" element={<ProtectedRoute><ScoreHistoryDetail /></ProtectedRoute>} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/terms-of-service" element={<TermsOfService />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </ErrorBoundary>
            </motion.div>
        </AnimatePresence>
    );
}

function ScoreAccessRoute({ children }) {
    const { id } = useParams();
    if (id?.startsWith("demo-")) return children;
    return <ProtectedRoute>{children}</ProtectedRoute>;
}

function AppContent() {
    const location = useLocation();
    const executiveMode = location.pathname.includes("/genz-credit-scoring/lender/")
        || location.pathname.includes("/genz-credit-scoring/admin");

    return (
        <div className="min-h-dvh relative">
            <RouteBackground />
            <ScrollManager />
            <ScrollProgress />
            <div className="relative z-10">
                <Navbar />
                <AnimatedRoutes />
            </div>
            <VibeChat hidden={executiveMode} />
            <Toaster position="top-center" offset={{ top: 76 }} mobileOffset={{ top: 76 }} theme="dark" richColors closeButton />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
